import {
  CatalogByCategory,
  CatalogCategory, ExtraCategory, GROUP_RELATIONS,
  MainCategoriesEnum, ProductNameEnum, ProductUiState, SelectedGroupMap, TextureCategory, UID
} from "@shared/core";
import { ETALON_MQ } from "../lib/ResponsiveScaler/constants";
import { getDefaultDataByProduct, keyFor } from "../store/localStorage";
import { ProductStateSlice } from "../store";
import { withImageSrc } from "../data/textures";

/** Return default group uid for a given category (or null). */
export function computeDefaultGroupForCategory(
  catalog: CatalogByCategory,
  category: MainCategoriesEnum
): string | null {
  const cat = catalog[category] as CatalogCategory | undefined;
  if (!cat || cat.type !== "texture") return null;

  const anyCat: any = cat;

  // 1) explicit initialGroup on category (if provided in DATA_TEXTURES_RELATION)
  if (typeof anyCat.initialGroup === "string") {
    return anyCat.initialGroup;
  }

  if (typeof anyCat.initialItem === "string") {
    return anyCat.initialItem;
  }

  // 2) fallback: first item that has .group
  const firstWithGroup = cat.items.find((i: any) => i.group);
  return (firstWithGroup as any)?.group ?? null;
}

/** Build default SelectedGroupMap for *all* categories in a catalog. */
export function buildDefaultSelectedGroupMap(
  catalog: CatalogByCategory
): SelectedGroupMap {
  const map: SelectedGroupMap = {};
  (Object.keys(catalog) as MainCategoriesEnum[]).forEach((catKey) => {
    const g = computeDefaultGroupForCategory(catalog, catKey);
    if (g) map[catKey] = g;
  });
  return map;
}

export function getInitialGroupForCategory(
  cat: TextureCategory
): string | undefined {

  const anyCat = cat as TextureCategory & { initialGroup?: string };
  if (typeof anyCat.initialGroup === "string") {
    return anyCat.initialGroup;
  }

  if (cat?.items?.length) {
    // fallback (1)
    const firstWithGroup = (cat.items as any[]).find(
      (i) => i.group
    );

    if (firstWithGroup?.group) {
      return firstWithGroup.group;
    }
  }

  // fallback (2)
  return GROUP_RELATIONS['__unknown__'].uid;
}

export function getInitialItemForCategory(
  cat: TextureCategory | ExtraCategory | undefined
): UID | null {
  if (!cat || !cat.items?.length) return null;
  // Do not fallback to the first item; only respect explicit initialItem
  /* @ts-ignore */
  return cat?.initialItem ?? null;
}

export function resolveSelectedGroupForCategory(
  ui: ProductUiState | undefined,
  category: MainCategoriesEnum,
  catalog: CatalogByCategory
): string | undefined {
  const cat = catalog[category];
  if (!cat || cat.type !== "texture") return undefined;

  const explicit =
    (ui as any)?.selectedGroup &&
    typeof (ui as any).selectedGroup[category] === "string"
      ? (ui as any).selectedGroup[category]
      : undefined;
  if (explicit) return explicit;

  const texCat = cat as TextureCategory & { initialGroup?: string };
  if (typeof texCat.initialGroup === "string") {
    return texCat.initialGroup;
  }

  /* @ts-ignore */
  const firstWithGroup = texCat.items.find((i) => i.group);
  return firstWithGroup?.group;
}

export function pxToVw(px: number, etalonWidth = ETALON_MQ): string {
  return `${(px / etalonWidth) * 100}vw`;
}

export function getLocalStorageData(product: ProductNameEnum): ProductStateSlice | null {
  const extraRaw = localStorage.getItem(keyFor(product));
  const extra = (() => {
    try {
      return JSON.parse(extraRaw || "{}");
    } catch {
      return null;
    }
  })();

  return extra
}

export function getSelectedImages(product: ProductNameEnum, categoriesToEmit: MainCategoriesEnum[] = []) {
  const storageData = getLocalStorageData(product);
  const productData = getDefaultDataByProduct(product);
  const textures = {};
  if (storageData?.config) {
    categoriesToEmit.forEach((cat) => {
      const storageChunk = storageData?.config?.[cat];
      if (storageChunk?.kind === 'single') {
        const needleUid = storageChunk.uid;
        const productDataChunk = productData?.[cat];
        if (productDataChunk?.items) {
          const needle = productDataChunk.items.find(el => el.uid === needleUid);
          if (needle) {
            const src = withImageSrc([needle])[0]?.src;
            if (src) {
              /* @ts-ignore */
              textures[cat] = new URL(src, window.location.origin).toString();
            }
          }
        }
      }
    }, {})
  }
  return textures;
}

export type TextureKey = "hull" | "fabric" | "flooring";
export type TextureUrls = Partial<Record<TextureKey, string>>;

function guessMimeFromUrl(url: string): string {
  const lower = url.toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".webp")) return "image/webp";
  return "application/octet-stream";
}

function fileExtFromMime(mime: string): string {
  if (mime === "image/png") return "png";
  if (mime === "image/jpeg") return "jpg";
  if (mime === "image/webp") return "webp";
  return "bin";
}

/**
 * Fetches texture images by URL and appends them to the given FormData.
 * Field names:
 *  - texture_hull
 *  - texture_fabric
 *  - texture_teak
 */
export async function appendTextureUrlsToFormData(
  formData: FormData,
  textureUrls: TextureUrls,
  opts?: {
    filenamePrefix?: string;
    fetchMode?: RequestMode;
    formDataKey?: string;
  }
): Promise<void> {
  const filenamePrefix = opts?.filenamePrefix ?? "texture";
  const formDataKey = opts?.formDataKey ?? "images";
  const fetchMode = opts?.fetchMode ?? "cors";

  const keys: Array<keyof TextureUrls> = ["hull", "flooring", "fabric"];

  for (const key of keys) {
    const url = textureUrls[key];
    if (!url) continue;

    const resp = await fetch(url, { mode: fetchMode });
    if (!resp.ok) {
      throw new Error(`Failed to fetch texture "${String(key)}" from ${url}: ${resp.status}`);
    }

    const blob = await resp.blob();
    const mime = blob.type || guessMimeFromUrl(url);
    const ext = fileExtFromMime(mime);

    const filename = `${filenamePrefix}_${String(key)}.${ext}`;
    formData.append(formDataKey, blob, filename);
  }
}

export function bytesToDataUrl(bytes: Uint8Array, mime = "image/png"): string | undefined {
  if (!bytes?.length) return;

  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    /* @ts-ignore */
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  const base64 = btoa(binary);
  return `data:${mime};base64,${base64}`;
}

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
